import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useClient } from 'shared/apollo';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';
import MuiTable from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableContainer from '@mui/material/TableContainer';
import {
  Autocomplete,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInbox, faTimes } from '@fortawesome/pro-light-svg-icons';
import {
  DonorAccount,
  QueuePortfolio,
  QueueInvestment,
  TargetPortfolio,
  getDonors,
  getPartnerQueue,
  getAdminAccountPortfolios,
  moveInvestmentToPortfolio,
} from './api';

const PageWrapper = styled.div`
  width: 100%;
  margin: 0 auto;
  padding: ${SPACERS.large};
  box-sizing: border-box;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACERS.small};
  margin-bottom: ${SPACERS.medium};
  .header-icon {
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
    background: ${COLORS.ctaPrimaryBackground};
    color: ${COLORS.ctaPrimary};
  }
  .heading {
    margin: 0;
  }
  .subheading {
    margin: 2px 0 0;
    color: ${COLORS.grayDark};
  }
`;

const Card = styled.div`
  background: ${COLORS.whiteBase};
  border: 1px solid #CFD9E4;
  border-radius: 12px;
  padding: ${SPACERS.medium};
  margin-bottom: ${SPACERS.medium};
  box-shadow: 0 2px 8px rgba(0, 115, 163, 0.05);
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${SPACERS.small};
`;

const StyledTableContainer = styled(TableContainer)`
  border: 1px solid #CFD9E4;
  border-radius: 12px;
  background: ${COLORS.whiteBase};
  td, th {
    font-family: inherit;
    font-size: 14px;
  }
  th {
    font-weight: 700;
    background: ${COLORS.greyLightest};
    color: ${COLORS.grayDark};
  }
  .mono {
    font-family: monospace;
    font-size: 13px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${SPACERS.extraLarge} ${SPACERS.large};
  color: ${COLORS.grayDark};
  background: ${COLORS.greyLightest};
  border-radius: 12px;
  border: 1px dashed ${COLORS.greyBase};
  .icon {
    font-size: 40px;
    color: ${COLORS.grayBase};
    margin-bottom: ${SPACERS.small};
  }
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: ${SPACERS.medium};
`;

const ErrorBanner = styled.div`
  background: ${COLORS.criticalBackground};
  color: ${COLORS.critical};
  border: 1px solid ${COLORS.critical};
  border-radius: 8px;
  padding: ${SPACERS.small} ${SPACERS.medium};
  margin-bottom: ${SPACERS.small};
`;


const MovingChip = styled.div`
  display: inline-flex;
  align-items: center;
  background: ${COLORS.ctaPrimaryBackground};
  color: ${COLORS.ctaPrimary};
  border: 1px solid ${COLORS.ctaPrimarySubdued};
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 12px;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DialogSubtitle = styled.p`
  margin: 0 0 14px;
  font-size: 14px;
  color: ${COLORS.grayDark};
`;

const ValidationHint = styled.p`
  margin: 4px 0 0;
  font-size: 12px;
  color: ${COLORS.grayDark};
`;


const formatMoney = (amount: number | null | undefined): string => {
  if (amount == null) return '—';
  return amount.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const PartnerQueue: React.FC = () => {
  const client = useClient();

  const [donors, setDonors] = useState<DonorAccount[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<number | ''>('');
  const [queuePortfolios, setQueuePortfolios] = useState<QueuePortfolio[]>([]);
  const [targetPortfolios, setTargetPortfolios] = useState<TargetPortfolio[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [loadingQueue, setLoadingQueue] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveTarget, setMoveTarget] = useState<TargetPortfolio | null>(null);
  const [moveLoading, setMoveLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getDonors(client)
      .then(res => {
        setDonors(res.data.partnerQueueAccounts);
      })
      .catch(() => setError('Failed to load donor accounts'));
  }, [client]);

  const loadQueue = useCallback(
    (accountId: number) => {
      setLoadingQueue(true);
      setQueuePortfolios([]);
      setTargetPortfolios([]);
      setSelectedIds(new Set());
      setMoveTarget(null);
      Promise.all([
        getPartnerQueue(client, accountId),
        getAdminAccountPortfolios(client, accountId),
      ])
        .then(([queueRes, portfoliosRes]) => {
          setQueuePortfolios(queueRes.data.partnerQueuePortfolios);
          setTargetPortfolios(portfoliosRes.data.adminAccountPortfolios);
        })
        .catch(() => setError('Failed to load partner queue'))
        .finally(() => setLoadingQueue(false));
    },
    [client],
  );

  const handleAccountChange = (accountId: number) => {
    setSelectedAccountId(accountId);
    loadQueue(accountId);
  };

  const allInvestments: (QueueInvestment & { portfolioName: string; portfolioYear: number | null; isUnassigned: boolean })[] =
    queuePortfolios
      .flatMap(p =>
        p.investments.map(inv => ({
          ...inv,
          portfolioName: p.name,
          portfolioYear: p.year ?? null,
          isUnassigned: p.name.startsWith('Partner Investment portfolio'),
        })),
      )
      .sort((a, b) => (a.isUnassigned === b.isUnassigned ? 0 : a.isUnassigned ? -1 : 1));

  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === allInvestments.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(allInvestments.map(i => i.dbId)));
    }
  };

  const handleMove = async () => {
    if (!moveTarget) return;
    setMoveLoading(true);
    for (const id of selectedIds) {
      try {
        await moveInvestmentToPortfolio(client, id, moveTarget.dbId);
      } catch { /* empty */ }
    }
    setMoveLoading(false);
    setMoveOpen(false);
    setMoveTarget(null);
    setSelectedIds(new Set());
    if (selectedAccountId) loadQueue(selectedAccountId as number);
  };

  const totalInvestments = allInvestments.length;

  const selectedInvestment = selectedIds.size === 1 ? allInvestments.find(i => selectedIds.has(i.dbId)) : null;
  const selectedLabel = selectedInvestment
    ? `"${selectedInvestment.programNameByDonor ?? 'Unnamed'}"`
    : `${selectedIds.size} investments`;

  return (
    <PageWrapper>
      <PageHeader>
        <div className="header-icon">
          <FontAwesomeIcon icon={faInbox} size="lg" />
        </div>
        <div>
          <TrueImpactText variant="heading-page" className="heading">
            Partner Queue
          </TrueImpactText>
          <TrueImpactText className="subheading">
            Investments from partner integrations pending assignment to a portfolio.
          </TrueImpactText>
        </div>
      </PageHeader>

      {error && <ErrorBanner>{error}</ErrorBanner>}

      <Card>
        <TrueImpactText variant="heading-subheading">Filters</TrueImpactText>
        <FormControl style={{ minWidth: 320 }}>
          <InputLabel>Select Donor Account</InputLabel>
          <Select
            value={selectedAccountId}
            onChange={e => handleAccountChange(e.target.value as number)}
            label="Select Donor Account"
            SelectDisplayProps={{ style: { fontSize: 14 } }}
          >
            {donors.map(d => (
              <MenuItem key={d.dbId} value={d.dbId}>
                {d.name} <span style={{ color: '#888', marginLeft: 6 }}>#{d.dbId}</span>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Card>

      {selectedAccountId !== '' && (
        <Card>
          <CardHeader>
            <TrueImpactText variant="heading-subheading">Investments</TrueImpactText>
            {!loadingQueue && selectedIds.size > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <TrueImpactText variant="body-small">{selectedIds.size} selected</TrueImpactText>
                <TrueImpactButton
                  variant="primary-large"
                  onClick={() => setMoveOpen(true)}
                  style={{ padding: '10px 16px', fontSize: '14px' }}
                >
                  Move to Portfolio
                </TrueImpactButton>
              </div>
            )}
          </CardHeader>

          {loadingQueue ? (
            <LoadingWrapper>
              <TrueImpactLoading />
            </LoadingWrapper>
          ) : queuePortfolios.length === 0 ? (
            <EmptyState>
              <div className="icon">
                <FontAwesomeIcon icon={faInbox} />
              </div>
              <TrueImpactText>No partner queue investments found for this account.</TrueImpactText>
            </EmptyState>
          ) : (
            <StyledTableContainer>
              <MuiTable>
                <TableHead>
                  <TableRow>
                    <TableCell style={{ width: 44 }}>
                      <Checkbox
                        size="small"
                        checked={allInvestments.length > 0 && selectedIds.size === allInvestments.length}
                        indeterminate={selectedIds.size > 0 && selectedIds.size < allInvestments.length}
                        onChange={toggleSelectAll}
                      />
                    </TableCell>
                    <TableCell>Program</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Site ID</TableCell>
                    <TableCell>Payment Date</TableCell>
                    <TableCell>Payment Type</TableCell>
                    <TableCell>Year</TableCell>
                    <TableCell>Portfolio</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {allInvestments.map(inv => (
                    <TableRow
                      key={inv.dbId}
                      hover
                      sx={inv.isUnassigned ? {
                        backgroundColor: 'rgba(255, 244, 220, 0.6)',
                        '&:hover': { backgroundColor: 'rgba(255, 237, 190, 0.8)' },
                      } : undefined}
                    >
                      <TableCell>
                        <Checkbox
                          size="small"
                          checked={selectedIds.has(inv.dbId)}
                          onChange={() => toggleSelect(inv.dbId)}
                        />
                      </TableCell>
                      <TableCell>{inv.programNameByDonor ?? '—'}</TableCell>
                      <TableCell align="right">{formatMoney(inv.cashAmount)}</TableCell>
                      <TableCell>{inv.siteId ?? '—'}</TableCell>
                      <TableCell>{inv.paymentDate ?? '—'}</TableCell>
                      <TableCell>
                        {inv.paymentType ? inv.paymentType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '—'}
                      </TableCell>
                      <TableCell>{inv.portfolioYear ?? '—'}</TableCell>
                      <TableCell>
                        {inv.isUnassigned
                          ? <Chip
                            label="Unassigned"
                            size="small"
                            sx={{ fontSize: 11, height: 20, backgroundColor: 'rgba(230, 155, 0, 0.12)', color: '#8a6200', fontWeight: 500 }}
                          />
                          : (inv.portfolioName ?? '—')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </MuiTable>
            </StyledTableContainer>
          )}
          {!loadingQueue && totalInvestments > 0 && (
            <div style={{ textAlign: 'right', marginTop: 8 }}>
              <TrueImpactText variant="body-small">
                {totalInvestments} investment{totalInvestments === 1 ? '' : 's'}
              </TrueImpactText>
            </div>
          )}
        </Card>
      )}

      <Dialog
        open={moveOpen}
        onClose={() => !moveLoading && setMoveOpen(false)}
        maxWidth="sm"
        fullWidth
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.55)' } }}
      >
        <DialogTitle sx={{
          fontFamily: 'inherit',
          fontSize: 18,
          fontWeight: 600,
          px: 3,
          pt: 2.5,
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          Move to Portfolio
          <IconButton
            onClick={() => !moveLoading && setMoveOpen(false)}
            disabled={moveLoading}
            size="small"
            sx={{ color: COLORS.grayDark, mr: -0.5 }}
          >
            <FontAwesomeIcon icon={faTimes} style={{ fontSize: 14 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 0.5, pb: 0 }}>
          <MovingChip>Moving: {selectedLabel}</MovingChip>
          <DialogSubtitle>
            Select the destination portfolio for the selected investment{selectedIds.size !== 1 ? 's' : ''}.
          </DialogSubtitle>
          <Autocomplete
            options={targetPortfolios}
            getOptionLabel={p => `${p.name}${p.year ? ` (${p.year})` : ''}`}
            value={moveTarget}
            onChange={(_e, val) => setMoveTarget(val)}
            isOptionEqualToValue={(a, b) => a.dbId === b.dbId}
            componentsProps={{ paper: { sx: { fontSize: 14 } } }}
            renderInput={params => (
              <TextField
                {...params}
                label="Choose a portfolio"
                inputProps={{ ...params.inputProps, style: { fontSize: 14 } }}
                InputLabelProps={{ style: { fontSize: 14 } }}
              />
            )}
          />
          {!moveTarget && (
            <ValidationHint>Required to continue</ValidationHint>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pt: 2, pb: 2.5, gap: 1.5 }}>
          <TrueImpactButton
            variant="secondary-large"
            onClick={() => setMoveOpen(false)}
            disabled={moveLoading}
            style={{ flex: 1, padding: '10px 16px', fontSize: '14px' }}
          >
            Cancel
          </TrueImpactButton>
          <Tooltip title={!moveTarget ? 'Please select a portfolio first' : ''} placement="top">
            <span style={{ flex: 1, display: 'flex' }}>
              <TrueImpactButton
                variant="primary-large"
                onClick={handleMove}
                disabled={!moveTarget || moveLoading}
                style={{ flex: 1, padding: '10px 16px', fontSize: '14px' }}
              >
                {moveLoading ? 'Moving…' : 'Move →'}
              </TrueImpactButton>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>
    </PageWrapper>
  );
};

export default PartnerQueue;
